/**
 * Preloads the WebRTC dictation connection on page load.
 *
 * Uses a synthetic silent audio stream for SDP negotiation — no microphone
 * permission prompt or browser mic indicator. The real mic is acquired later
 * when the user clicks "Start dictation" via replaceTrack().
 *
 * Only preconnects when mic permission is already "granted" (i.e., the user
 * has used dictation before), to avoid wasting Lambda/OpenAI resources.
 *
 * The session is negotiated for one *surface* and cannot serve the other, so
 * this follows `dictationSurfaceIntentAtom`: an open Indy surface warms an Indy
 * session, and closing it falls back to Assessment. The surface is an effect
 * dependency, so a change tears the old session down and warms the new one.
 *
 * Mount `usePreconnectDictation` ONCE per page. Two live instances would fight
 * over the singleton: one unmounting would call stopSession() out from under
 * the other. `useDictationWarmup` holds no lifecycle and is safe anywhere.
 */
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { dictationSurfaceIntentAtom } from "~/atoms/dictation";
import { useCurrentUser } from "~/auth/UserContext";
import {
  type DictationSurface,
  webrtcTranscriptionService,
} from "~/services/webrtcTranscription";
import {
  checkMicrophonePermission,
  isBrowserSupported,
} from "~/utils/textInputUtils";

/**
 * How long to stand down after a failed warm. Without this a keystroke-frequency
 * caller turns a failing proxy into one negotiation per character — a new
 * AudioContext, RTCPeerConnection and Lambda invocation each time.
 */
const WARM_FAILURE_BACKOFF_MS = 30_000;

/**
 * Returns a callback that warms the dictation connection on demand, for
 * surfaces that know a dictation is imminent — the composer gaining focus, or
 * a push-to-talk hold starting.
 *
 * Idempotent and cheap to call repeatedly: a healthy session short-circuits
 * synchronously, before the async permission round trip, so this is safe to
 * wire to high-frequency events like focus and keystrokes. This is what
 * re-establishes a connection the service's 5-minute idle timeout closed, so
 * the user does not pay for the reconnect at the moment they reach for the mic.
 *
 * @param surface The surface this caller will actually dictate with. Pass it
 *   whenever it is known statically. The intent atom is claimed by a parent
 *   effect, and a child's focus handler can run first — an Indy composer that
 *   warmed on `intent ?? "assessment"` negotiates an Assessment-shaped session
 *   that `usePreconnectDictation` then tears down a tick later, wasting the
 *   session and leaving nothing warm.
 */
export function useDictationWarmup(surface?: DictationSurface): () => void {
  const { user } = useCurrentUser();
  const surfaceIntent = useAtomValue(dictationSurfaceIntentAtom);

  // Read through a ref so the callback identity stays stable — it is wired to
  // focus and keydown handlers that would otherwise re-subscribe per render.
  const depsRef = useRef({ user, surfaceIntent, surface });
  depsRef.current = { user, surfaceIntent, surface };
  const failedAtRef = useRef(0);

  return useCallback(() => {
    const {
      user: u,
      surfaceIntent: intent,
      surface: explicit,
    } = depsRef.current;
    if (!isBrowserSupported() || !u?.id) return;

    // Nothing to warm, and skipping the permission query keeps a per-keystroke
    // caller free. A live recording owns the connection outright — preconnect()
    // would renegotiate it out from under the microphone.
    const status = webrtcTranscriptionService.getStatus();
    if (status.isConnected || status.isRecording) return;

    if (Date.now() - failedAtRef.current < WARM_FAILURE_BACKOFF_MS) return;

    void (async () => {
      const permission = await checkMicrophonePermission();
      if (permission !== "granted") return;
      // A dictation can start inside the permission round trip.
      if (webrtcTranscriptionService.getStatus().isRecording) return;
      try {
        await webrtcTranscriptionService.preconnect(
          explicit ?? intent ?? "assessment",
        );
        failedAtRef.current = 0;
      } catch (err) {
        failedAtRef.current = Date.now();
        console.warn("[dictation] on-demand preconnect failed:", err);
      }
    })();
  }, []);
}

/**
 * @param defaultSurface Surface to warm before any Indy surface claims the
 *   intent. Pages whose primary dictation target is Indy pass "indy" so the
 *   very first negotiation is the right shape — a child setting the intent
 *   atom cannot help, since the parent effect has already fired by then.
 */
export function usePreconnectDictation(
  defaultSurface: DictationSurface = "assessment",
): void {
  const { user } = useCurrentUser();
  const surfaceIntent = useAtomValue(dictationSurfaceIntentAtom);
  const surface = surfaceIntent ?? defaultSurface;

  // Distinguishes a real unmount from a surface change. Declared BEFORE the
  // effect below so React runs its cleanup first on unmount (cleanups fire in
  // declaration order), letting that cleanup tell the two cases apart.
  const unmountingRef = useRef(false);
  useEffect(() => {
    unmountingRef.current = false;
    return () => {
      unmountingRef.current = true;
    };
  }, []);

  // Preconnect on mount and whenever the target surface changes; tear down on
  // unmount. Single effect owns the full lifecycle to avoid cleanup races.
  useEffect(() => {
    if (!isBrowserSupported() || !user?.id) return;

    let cancelled = false;

    const preload = async () => {
      // Never renegotiate over a live dictation. `surface` flips at runtime —
      // opening the Indy panel over an appointment page claims the session —
      // and preconnect() would tear down the connection the physician is
      // currently speaking into.
      if (webrtcTranscriptionService.getStatus().isRecording) return;

      // Only preconnect if mic permission is already granted
      // (avoids wasting resources for users who haven't used dictation)
      const permission = await checkMicrophonePermission();
      if (permission !== "granted" || cancelled) return;
      // Re-check: the permission round trip is async, and a dictation can
      // start inside it.
      if (webrtcTranscriptionService.getStatus().isRecording) return;

      try {
        await webrtcTranscriptionService.preconnect(surface);
      } catch (err) {
        if (!cancelled) {
          console.warn(
            "[dictation] preconnect failed, will connect on first use:",
            err,
          );
        }
      }
    };

    void preload();

    return () => {
      cancelled = true;
      // On a *surface change*, leave a live session alone. stopSession() runs
      // cleanup(), which stops the microphone without firing onDisconnected —
      // the manager's status atom would stay "active" and the UI would show a
      // red live mic over a dead connection, transcribing nothing, with no
      // error surfaced. The recording owns the connection until it ends; the
      // next startRecording() renegotiates if the surface moved on.
      // On a real unmount we still tear down, or navigating away mid-dictation
      // would strand an open microphone.
      if (
        !unmountingRef.current &&
        webrtcTranscriptionService.getStatus().isRecording
      ) {
        return;
      }
      webrtcTranscriptionService.stopSession();
    };
  }, [user?.id, surface]);
}
